import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { fetchFluxTresorerie } from '../../api/tresorerieApi';

const FluxTresorerie = () => {
  const [flux, setFlux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtrePeriode, setFiltrePeriode] = useState('mois');
  const [filtreCategorie, setFiltreCategorie] = useState('tous');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  // Chargement des flux de trésorerie
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchFluxTresorerie({ periode: filtrePeriode, categorie: filtreCategorie, dateDebut, dateFin });
        setFlux(data);
      } catch (err) {
        setError("Erreur lors du chargement des flux de trésorerie.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filtrePeriode, filtreCategorie, dateDebut, dateFin]);

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Calcul du solde total
  const soldeTotal = flux.reduce((acc, f) => acc + (f.type === 'entree' ? f.montant : -f.montant), 0);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Flux de trésorerie</Typography>

      {/* Filtres */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Période</InputLabel>
          <Select value={filtrePeriode} onChange={(e) => setFiltrePeriode(e.target.value)}>
            <MenuItem value="mois">Mois</MenuItem>
            <MenuItem value="trimestre">Trimestre</MenuItem>
            <MenuItem value="annee">Année</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Catégorie</InputLabel>
          <Select value={filtreCategorie} onChange={(e) => setFiltreCategorie(e.target.value)}>
            <MenuItem value="tous">Tous</MenuItem>
            <MenuItem value="vente">Ventes</MenuItem>
            <MenuItem value="achat">Achats</MenuItem>
            <MenuItem value="salaire">Salaires</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Date de début"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateDebut}
          onChange={(e) => setDateDebut(e.target.value)}
        />
        <TextField
          label="Date de fin"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateFin}
          onChange={(e) => setDateFin(e.target.value)}
        />
      </Box>

      {/* Tableau des flux */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Libellé</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Montant (€)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flux.map((f) => (
              <TableRow key={f.id}>
                <TableCell>{formatDate(f.date)}</TableCell>
                <TableCell>{f.libelle}</TableCell>
                <TableCell>{f.categorie}</TableCell>
                <TableCell>{f.type}</TableCell>
                <TableCell align="right" sx={{ color: f.type === 'entree' ? 'green' : 'red' }}>
                  {f.type === 'entree' ? `+${f.montant}` : `-${f.montant}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Solde total */}
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="h6">Solde total : {soldeTotal} €</Typography>
      </Box>
    </Box>
  );
};

export default FluxTresorerie;
